/**
 * Ticket sources — HOW the person got in touch (Kai, 2026-09-03, from the
 * reference's own configured list: Portal, Email, Phone, Walk-up, MS Teams,
 * Talkback, Monitoring). A fixed list like priorities — engines and reports
 * need stable keys — with display labels here so every surface renders the
 * same words. `portal`/`email`/`monitoring`/`api` are set by machinery
 * (portal submit, mail intake, alert ingestion, API clients); the rest are
 * what an agent picks when raising a ticket on someone's behalf. `agent` is
 * the legacy value older rows carry.
 */
export const TICKET_SOURCES = [
  { key: 'portal', label: 'Portal' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'walk_up', label: 'Walk-up' },
  { key: 'ms_teams', label: 'MS Teams' },
  { key: 'talkback', label: 'Talkback' },
  { key: 'monitoring', label: 'Monitoring alert' },
  { key: 'api', label: 'API' },
  { key: 'agent', label: 'Agent (direct)' },
] as const;

export type TicketSource = (typeof TICKET_SOURCES)[number]['key'];

export const SOURCE_KEYS = TICKET_SOURCES.map((s) => s.key) as [TicketSource, ...TicketSource[]];

/** The channels an AGENT may pick when logging a ticket by hand — the
 *  machine-set ones aren't offered (a human claiming 'monitoring' would put
 *  a lie in the reports the alert pipeline feeds). */
export const AGENT_PICKABLE_SOURCES = TICKET_SOURCES.filter((s) =>
  ['phone', 'walk_up', 'ms_teams', 'talkback', 'email', 'agent'].includes(s.key),
);

/**
 * The channels an agent may set when CORRECTING a ticket after the fact.
 *
 * Wider than `AGENT_PICKABLE_SOURCES` by exactly one: `portal`. Raising a
 * ticket by hand is never a portal submission, so it is not offered there —
 * but a catalogue request now defaults to `portal`, and an agent correcting a
 * mis-set source has to be able to put it back. The machine-only channels
 * (`monitoring`, `api`) stay out for the same reason as before: a human
 * claiming them puts a lie in the reports the alert pipeline feeds.
 */
export const AGENT_EDITABLE_SOURCES = TICKET_SOURCES.filter((s) =>
  ['portal', 'email', 'phone', 'walk_up', 'ms_teams', 'talkback', 'agent'].includes(s.key),
);

export function sourceLabel(key: string | null | undefined): string {
  return TICKET_SOURCES.find((s) => s.key === key)?.label ?? key ?? '—';
}
